/**
 * Leaders per category, away row then home row, each with the team mark, the
 * jersey number, the player's photo (ISSUE-983's shared PlayerHeadshot, a
 * silhouette when there is none) and the category's stat columns. Shared by
 * ISSUE-988's season leaders and ISSUE-989's game leaders. A game performer
 * with a resolved player id links to the player page (ISSUE-989); season
 * leaders do not. Nothing is computed here. ISSUE-988 WI-23.
 */
import { FOCUS_RING } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import type { typePlayerSearch } from "../../routeState/playerSearch";
import { PlayerHeadshot } from "../playerProfile/PlayerHeadshot";
import type {
  typeLeaderCategory,
  typeLeaderPlayer,
  typeMatchupTeamMark,
} from "./matchupTypes";
import { TeamMark } from "./TeamMark";
import { GameSectionCard } from "./GameSectionCard";

/** Six columns (ISSUE-982's football categories) do not fit one row at 390px,
 *  so they wrap into two rows of three (ISSUE-988). Five columns do not fit
 *  either: a game performer's "32/48" needs about 46px and a phone leaves
 *  about 43px per column, measured in Chromium by matchupLiveGeometry.e2e.ts,
 *  so five wrap into three and two below sm and sit on one row from sm up
 *  (ISSUE-989). */
const statGridClass = (count: number): string => {
  switch (count) {
    case 3:
      return "grid-cols-3";
    case 4:
      return "grid-cols-4";
    case 5:
      return "grid-cols-3 sm:grid-cols-5";
    case 6:
      return "grid-cols-3";
    default:
      return "grid-cols-5";
  }
};

const LeaderRow = ({
  team,
  player,
  columns,
}: {
  team: typeMatchupTeamMark;
  player: typeLeaderPlayer;
  columns: readonly string[];
}): ReactNode => {
  const jerseyNumber = player.jerseyNumber?.trim() ?? "";
  const content = (
    <>
      <div className="flex w-10 shrink-0 flex-col items-center gap-1">
        <TeamMark
          name={team.name}
          abbreviation={team.plateText}
          logoUrl={team.logoUrl}
          size="sm"
        />
        {jerseyNumber === "" ? null : (
          <span
            data-testid="leader-jersey"
            className="text-small text-ink-secondary"
          >
            {jerseyNumber}
          </span>
        )}
      </div>
      <PlayerHeadshot headshotUrl={player.headshotUrl ?? null} size="md" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="truncate text-body-strong text-ink">
          {player.name}
        </span>
        <div
          data-testid="leader-stat-grid"
          className={`grid gap-1 ${statGridClass(columns.length)}`}
        >
          {columns.map((column, index) => (
            <div
              key={column}
              data-testid="leader-stat-cell"
              className="flex min-w-0 flex-col"
            >
              <span className="truncate text-body text-ink">
                {player.values[index] ?? ""}
              </span>
              <span className="text-small text-ink-secondary">{column}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
  const playerId = player.playerId ?? "";
  if (
    playerId !== "" &&
    team.teamId !== undefined &&
    team.abbreviation !== undefined
  ) {
    const search: typePlayerSearch = {
      playerId,
      name: player.name,
      teamId: team.teamId,
      teamName: team.name,
      teamAbbreviation: team.abbreviation,
      ...(jerseyNumber === "" ? {} : { jerseyNumber }),
    };
    return (
      <div data-testid="leader-row" className="py-2">
        <Link
          to="/scores/player"
          search={search}
          data-testid="leader-player-link"
          className={`flex min-h-touch-target items-center gap-2 text-ink no-underline ${FOCUS_RING}`}
        >
          {content}
        </Link>
      </div>
    );
  }
  return (
    <div data-testid="leader-row" className="flex items-center gap-2 py-2">
      {content}
    </div>
  );
};

export const LeadersSection = ({
  title,
  categories,
  away,
  home,
}: {
  title: string;
  categories: readonly typeLeaderCategory[];
  away: typeMatchupTeamMark;
  home: typeMatchupTeamMark;
}): ReactNode => (
  <GameSectionCard testId="matchup-leaders" title={title}>
    {categories.map((category) => (
      <div key={category.key} data-testid="leader-category">
        <h4 className="pt-2 pb-1 text-small font-semibold text-ink-secondary uppercase">
          {category.title}
        </h4>
        {category.away ? (
          <LeaderRow
            team={away}
            player={category.away}
            columns={category.columns}
          />
        ) : null}
        {category.home ? (
          <LeaderRow
            team={home}
            player={category.home}
            columns={category.columns}
          />
        ) : null}
      </div>
    ))}
  </GameSectionCard>
);
